import type {
  CallerIdStrategy,
  DialStrategy,
  DialerSettings,
  QuickAction,
  RetryStep,
  UnansweredOutcome,
} from '@/shared/api/adminApi';

/** The 7 dial strategies with the user-facing Hebrew copy. */
export interface StrategyMeta {
  value: DialStrategy;
  label: string;
  description: string;
  discouraged?: boolean;
}

export const DIAL_STRATEGIES: StrategyMeta[] = [
  {
    value: 'hot_blend_newest',
    label: 'לידים חמים',
    description:
      'המערכת תדאג שכל הלידים יקבלו שיחה אחת ולאחר מכן תשלב בין כל הלידים שלא ענו, בדגש על הלידים החדשים ביותר שנוצרו. מומלץ לעסקים שבד"כ לא מסיימים להתקשר לכל הלידים שלהם בכל יום עבודה.',
  },
  {
    value: 'hot_oldest_first',
    label: 'לידים חמים - סידור שיחות מהישן לחדש',
    description:
      'המערכת תדאג שכל הלידים בקמפיין יקבלו שיחה בסידור מהכי מעט שיחות להכי הרבה שיחות. סידור הלידים יהיה מהישן לחדש.',
  },
  {
    value: 'hot_blend_attempts',
    label: 'לידים חמים - דגש על ניסיונות חיוג',
    description:
      'המערכת תדאג שכל הלידים שיקבלו שיחה אחת ולאחר מכן תשלב בין כל הלידים שלא ענו, בדגש על הלידים החדשים ביותר שנוצרו. מומלץ לעסקים שבד"כ מסיימים להתקשר לכל הלידים שלהם בכל יום עבודה.',
  },
  {
    value: 'hot_new_before_followup',
    label: 'לידים חמים - תיעדוף לידים חדשים לפני פולו-אפ (לא מומלץ)',
    description: 'לידים חדשים יקבלו תיעדוף יותר גבוה מפולו-אפים לשיחה הראשונה.',
    discouraged: true,
  },
  {
    value: 'cold_standard',
    label: 'לידים קרים',
    description:
      'שיחה בסידור מהכי מעט שיחות להכי הרבה שיחות. פולואפים יקבלו שיחה במועד שקבעו.',
  },
  {
    value: 'cold_ignore_followups',
    label: 'לידים קרים - התעלמות מפולואפים (לא מומלץ)',
    description:
      'המערכת לא תתקשר לפולו-אפים במועד שלהם אלא אם הנציגים סיימו להתקשר לכל הלידים שעוד לא שוחחתם איתם. להפעלה זמנית כשהצטברו המון לידים חדשים.',
    discouraged: true,
  },
  {
    value: 'cold_followups_first',
    label: 'לידים קרים - התעלמות מלידים חדשים (לא מומלץ)',
    description:
      'המערכת תתעדף את הפולו-אפים במועד שקבעו לפני לידים חדשים. להפעלה זמנית כשהצטברו המון פולו-אפים.',
    discouraged: true,
  },
];

export function strategyMeta(value: string | undefined): StrategyMeta | undefined {
  return DIAL_STRATEGIES.find((s) => s.value === value);
}

export const CALLER_ID_STRATEGY_LABEL: Record<CallerIdStrategy, string> = {
  fixed: 'מספר קבוע',
  agent: 'המספר של הנציג שמחייג',
  rotate: 'החלפה אחרי X ללא מענה',
  random: 'אקראי בכל שיחה',
};

export const UNANSWERED_OUTCOME_LABEL: Record<UnansweredOutcome, string> = {
  no_answer: 'לא ענה',
  busy: 'תפוס',
  failed: 'נכשל',
  machine: 'משיבון',
};

export const UNANSWERED_OUTCOMES: UnansweredOutcome[] = ['no_answer', 'busy', 'failed', 'machine'];

/**
 * Build the PUT/PATCH payload from an edited draft.
 *
 * `base` is what we started from (for tenant defaults it's the full defaults;
 * for a campaign it's `{}` so we only emit keys the user actually touched).
 * A key is included only when it's present in `draft` AND differs from `base`
 * — this lets per-campaign edits inherit unset keys from the tenant default.
 */
export function buildSettingsPayload(
  draft: Partial<DialerSettings>,
  base: Partial<DialerSettings>,
): Partial<DialerSettings> {
  const out: Partial<DialerSettings> = {};
  (Object.keys(draft) as (keyof DialerSettings)[]).forEach((key) => {
    const value = draft[key];
    if (value === undefined) return;
    if (JSON.stringify(value) === JSON.stringify(base[key])) return;
    // @ts-expect-error — key/value pairing is sound across the union
    out[key] = value;
  });
  return out;
}

/** A blank retry step for the ladder editor. */
export function emptyRetryStep(): RetryStep {
  return { attempts: 1, interval_min: 60 };
}

/** A blank quick action for the actions editor. */
export function emptyQuickAction(): QuickAction {
  return { label: '', type: 'url', url: '' };
}

/**
 * ברירות המחדל של הטוגלים — **זהות** ל-`CampaignSettings::DEFAULTS` בשרת.
 * 🔴 07/09/2026: הטופס נפל ל-`?? false` בזמן שהשרת מניח true, וקמפיין חדש
 * שמר `auto_dial_next:false` בלי שאיש בחר בזה — והחייגן "לא המשיך".
 */
export const DIALER_TOGGLE_DEFAULTS = {
  wrapup_required: true,
  wrapup_blocks_next: true,
  auto_dial_next: true,
  transfer_allowed: false,
  amd_enabled: false,
} as const;
